import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useNavigate } from "react-router-dom";
import { api, jsonBody } from "../api";
import type { Client, Pagination } from "../types";
import { PageHeader } from "../components/PageHeader";
import { StatusChip } from "../components/StatusChip";

export function ClientsPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const result = await api<{ clients: Client[]; pagination: Pagination }>(
        `/admin/clients?search=${encodeURIComponent(search)}&status=${status}`,
      );
      setClients(result.clients);
      setPagination(result.pagination);
      setError("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load clients");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const id = setTimeout(() => void load(), 250);
    return () => clearTimeout(id);
  }, [search, status]);
  return (
    <>
      <PageHeader
        title="Clients"
        subtitle="Provision applications, manage quotas, credentials and retention."
        actions={
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => setOpen(true)}
          >
            New client
          </Button>
        }
      />
      <Card>
        <Box p={2} display="flex" gap={1.5}>
          <TextField
            size="small"
            label="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 260 }}
          />
          <TextField
            select
            size="small"
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="SUSPENDED">Suspended</MenuItem>
            <MenuItem value="DELETED">Deleted</MenuItem>
          </TextField>
        </Box>
        {error && <Alert severity="error">{error}</Alert>}
        {loading ? (
          <Box p={5} textAlign="center">
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Client</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Usage</TableCell>
                <TableCell>Created</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.map((client) => {
                const pct = client.monthlyImageLimit
                  ? (client.monthlyImageUsed / client.monthlyImageLimit) * 100
                  : 100;
                return (
                  <TableRow
                    hover
                    key={client.id}
                    onClick={() => navigate(`/clients/${client.id}`)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      <Typography fontWeight={750}>{client.name}</Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        className="mono"
                      >
                        {client.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip value={client.status} />
                    </TableCell>
                    <TableCell>
                      <Chip label={client.plan} size="small" />
                    </TableCell>
                    <TableCell sx={{ minWidth: 180 }}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption">
                          {client.monthlyImageUsed} / {client.monthlyImageLimit}
                        </Typography>
                        <Typography variant="caption">
                          {Math.round(pct)}%
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, pct)}
                        color={
                          pct >= 100
                            ? "error"
                            : pct >= 90
                              ? "warning"
                              : "primary"
                        }
                        sx={{ height: 6, borderRadius: 4 }}
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(client.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Open">
                        <IconButton size="small">
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        <Box p={2}>
          <Typography variant="caption" color="text.secondary">
            {pagination?.total ?? 0} clients
          </Typography>
        </Box>
      </Card>
      <CreateClientDialog
        open={open}
        onClose={() => setOpen(false)}
        onCreated={(client) => {
          setOpen(false);
          navigate(`/clients/${client.id}`);
        }}
      />
    </>
  );
}

function CreateClientDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (client: Client) => void;
}) {
  const [form, setForm] = useState({
    name: "",
    plan: "FREE",
    monthlyImageLimit: "100",
    priorityWeight: "1",
    defaultCurrency: "LKR",
    retentionDays: "30",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const field =
    (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm({ ...form, [key]: e.target.value });
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Create client</DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          <TextField
            label="Client name"
            value={form.name}
            onChange={field("name")}
            required
          />
          <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
            <TextField
              label="Plan"
              value={form.plan}
              onChange={field("plan")}
              fullWidth
            />
            <TextField
              label="Default currency"
              value={form.defaultCurrency}
              onChange={field("defaultCurrency")}
              fullWidth
            />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
            <TextField
              type="number"
              label="Monthly image limit"
              value={form.monthlyImageLimit}
              onChange={field("monthlyImageLimit")}
              fullWidth
            />
            <TextField
              type="number"
              label="Priority weight"
              value={form.priorityWeight}
              onChange={field("priorityWeight")}
              fullWidth
            />
          </Stack>
          <TextField
            type="number"
            label="Retention days"
            value={form.retentionDays}
            onChange={field("retentionDays")}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={busy || !form.name}
          onClick={async () => {
            setBusy(true);
            try {
              const result = await api<{ client: Client }>("/admin/clients", {
                method: "POST",
                body: jsonBody({
                  ...form,
                  monthlyImageLimit: Number(form.monthlyImageLimit),
                  priorityWeight: Number(form.priorityWeight),
                  retentionDays: Number(form.retentionDays),
                }),
              });
              onCreated(result.client);
            } catch (e) {
              setError(e instanceof Error ? e.message : "Create failed");
            } finally {
              setBusy(false);
            }
          }}
        >
          Create client
        </Button>
      </DialogActions>
    </Dialog>
  );
}
